"use client";
import React, { useState } from "react";
import { CourseAuditLinkProps } from "@/components/CourseContent/EnrollButton/types";

import {
  ElementType,
  ElementClickedWebElement,
} from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import hasAuditSeat from "@/lib/course/utils/hasAuditSeat";
import useTranslations from "@/i18n/useTranslations";
import useCourseEnrollment from "../../hooks/useCourseEnrollment";
import AuditLinkButton from "./variants/AuditLinkButton";
import AuditLinkIcon from "./variants/AuditLinkIcon";
import AuditLinkAnchor from "./variants/AuditLinkAnchor";
import useWebview from "@/webview/useWebview";

type AuditLinkProps = CourseAuditLinkProps & {
  variant?: "anchor" | "button" | "icon";
};

function AuditLink({
  className = "",
  courseRun,
  course,
  name = "",
  location = "",
  variant = "button",
}: AuditLinkProps) {
  const [isDisabled, setDisabled] = useState(false);
  const t = useTranslations("Course");
  const {
    isLoggedIn,
    isUserEnrolledInRun,
    isCourseEnrollable,
    handleEnrollment,
    paidLeadValues,
    isCourseArchived,
    emailOptIn,
  } = useCourseEnrollment({
    courseRun,
    course,
  });

  //  Verified-only courses (no audit seat across active runs) must
  // never render an audit CTA, regardless of Monarch's auditCourseLabel.
  // Gate sits at the course level via getCoursePaymentOption so it stays
  // consistent with `showCourseModes` (the existing PAYMENT_UPGRADABLE gate).
  const courseHasAuditSeat = hasAuditSeat(course);

  const { isWebview, getWebviewEnrollmentUrl } = useWebview();

  //TODO: Replace with real API DATA
  const isEnterpriseUser = false;

  // Prefer Monarch copy when present; fall back to i18n so Verify+Audit
  // courses still show Audit when leadWithPaid targeting misses (e.g. seat order).
  const auditCourseLabel = paidLeadValues?.auditCourseLabel || t("auditCourse");

  const sharedElementProps = {
    elementType: ElementType.Button,
    location,
    htmlId: "audit-cta",
    text: "Audit course",
    position: "2",
  };

  const webElement: ElementClickedWebElement = {
    ...sharedElementProps,
    name,
  };

  const certWebElement: ElementClickedWebElement = {
    ...sharedElementProps,
    name: "course-conversion",
  };

  const { elementClicked } = useElementEvents({
    webElement,
  });

  const { elementClicked: certElementClicked } = useElementEvents({
    webElement: certWebElement,
  });

  const onEnroll = () => {
    if (!isDisabled) {
      setDisabled(true);

      if (location.startsWith("cert-module")) {
        certElementClicked({ webElement: certWebElement });
      } else {
        elementClicked({ webElement });
      }

      if (isEnterpriseUser) {
        //TODO: OPEN ENTERPRISE MODEL
      } else {
        if (isWebview) {
          window.location.href = getWebviewEnrollmentUrl(
            courseRun.key,
            emailOptIn,
          );
        } else {
          handleEnrollment("audit-cta");
        }
      }
    }
  };

  const auditCourse = isCourseArchived
    ? null
    : (() => {
        switch (variant) {
          case "button":
            return (
              <AuditLinkButton
                onEnroll={onEnroll}
                auditCourseLabel={auditCourseLabel}
                isDisabled={isDisabled}
                className={className}
              />
            );
          case "icon":
            return (
              <AuditLinkIcon
                onEnroll={onEnroll}
                auditCourseLabel={auditCourseLabel}
                isDisabled={isDisabled}
                className={className}
              />
            );
          case "anchor":
            return (
              <AuditLinkAnchor
                onEnroll={onEnroll}
                auditCourseLabel={auditCourseLabel}
                isDisabled={isDisabled}
                className={className}
              />
            );
          default:
            return (
              <AuditLinkButton
                onEnroll={onEnroll}
                auditCourseLabel={auditCourseLabel}
                isDisabled={isDisabled}
                className={className}
              />
            );
        }
      })();

  if (!courseHasAuditSeat) {
    return null;
  }

  const shouldShowAudit =
    isCourseEnrollable && (!isLoggedIn || !isUserEnrolledInRun);

  return <>{shouldShowAudit ? auditCourse : null}</>;
}

export default AuditLink;
